import {
  SimulationMessageType,
  SimulationTimeoutTime,
  Distribution
} from './constants'
import moment from 'moment'
import { from, min, max } from '@/helpers/range'
import { last, isOutside } from '@/helpers/array'
import { hasEditor } from '@/helpers/env'

let simulationSocket

export default {
  namespaced: true,
  state: {
    error: null,
    startDate: null,
    endDate: null,
    currentDateStep: 86400000 >> 2,
    currentDate: null,
    bufferedDate: null,
    timeoutID: null,
    hasEnded: false,
    hasStarted: false,
    isPlaying: false,
    isBuffering: true,
    currentIndex: 0,
    bufferedMax: null,
    bufferedMin: null,
    bufferedIndex: 0,
    buffer: []
  },
  getters: {
    start (state) {
      // The first date is always different from the specified date because
      // in the back the simulation always starts the next day we specified.
      // ¯\_(ツ)_/¯
      return state.startDate && moment(state.startDate).add(1, 'd').toDate().getTime()
    },
    end (state) {
      return state.endDate && state.endDate.getTime()
    },
    current (state) {
      return state.currentDate && state.currentDate.getTime()
    },
    buffered (state) {
      return state.bufferedDate && state.bufferedDate.getTime()
    },
    progress (_, getters) {
      return from(getters.current, getters.start, getters.end)
    },
    days (state) {
      return moment(state.endDate).diff(moment(state.startDate).add(1, 'd'), 'days')
    },
    data (state) {
      if (state.buffer.length === 0) {
        return null
      }
      if (isOutside(state.buffer, state.currentIndex)) {
        throw new Error(`Invalid currentIndex ${state.currentIndex}`)
      }
      return state.buffer[state.currentIndex]
    },
    precipitationAndIrrigation (_, getters) {
      if (!getters.data) {
        return 0
      }
      return getters.data.PcpIrri
    },
    appliedFertilizer (_, getters) {
      if (!getters.data) {
        return 0
      }
      return getters.data.FertStep * 1440
    },
    relativeNitrogenUptake (_, getters) {
      if (!getters.data) {
        return 0
      }
      return getters.data.RelativeNUptake * 100
    },
    nitrogenUptake (_, getters) {
      if (!getters.data) {
        return 0
      }
      return getters.data.NUptakeStep * 1440
    },
    nitrogenLeaching (_, getters) {
      if (!getters.data) {
        return 0
      }
      const { layers } = getters.data
      return last(layers).NF
    }
  },
  mutations: {
    setStartDate (state, startDate) {
      state.startDate = startDate
      state.bufferedDate = startDate
      state.currentDate = startDate
    },
    setEndDate (state, endDate) {
      state.endDate = endDate
    },
    setDates (state, {
      startDate,
      endDate
    }) {
      state.startDate = startDate
      state.bufferedDate = startDate
      state.currentDate = startDate
      state.endDate = endDate
    },
    setCurrentDate (state, currentDate) {
      state.currentDate = currentDate
    },
    error (state, payload) {
      state.error = payload
      state.isBuffering = false
    },
    add (state, payload) {
      payload.currentDate = moment(payload.Date, 'DD.MM.YYYY', false).toDate()
      // The first date is always different from the specified date because
      // in the back the simulation always starts the next day we specified.
      // ¯\_(ツ)_/¯
      if (state.buffer.length === 0) {
        //  state.startDate = payload.currentDate
        state.currentDate = payload.currentDate
      }
      // Update bufferedDate to the last Date
      // we receive from the simulation WebSocket.
      state.bufferedDate = payload.currentDate
      // state.bufferedDate = moment(payload.Date, 'YYYY-MM-DD', false).toDate()
      state.bufferedIndex = (state.buffer.push(payload) - 1)
      // We create maximums and minimums.
      for (const name in payload) {
        if (name === 'type'
          && name === 'Date') {
          continue
        }

        // We need to take care of layers differently because we need to
        // calculate max and min for every layer and taking care of
        // object structure.
        if (name === 'layers') {
          for (let index = 0; index < payload.layers.length; index++) {
            const layer = payload.layers[index]
            if (!state.bufferedMax.layers[index]) {
              state.bufferedMax.layers[index] = {}
            }
            if (!state.bufferedMin.layers[index]) {
              state.bufferedMin.layers[index] = {}
            }
            for (const name in layer) {
              max(state.bufferedMax.layers[index], name, layer[name], 1e-4)
              min(state.bufferedMin.layers[index], name, layer[name], 0)
            }
          }
        } else {
          max(state.bufferedMax, name, payload[name], 1e-4)
          min(state.bufferedMin, name, payload[name], 0)
        }
      }
    },
    ended (state) {
      state.isBuffering = false
    },
    started (state) {
      state.error = null
      state.buffer = []
      state.bufferedIndex = 0
      state.isBuffering = true
      state.isPlaying = false
      state.hasEnded = false
      state.hasStarted = true
      state.currentDate = new Date(state.startDate)
      state.currentIndex = 0
      state.bufferedMin = {
        layers: []
      }
      state.bufferedMax = {
        layers: []
      }
    },
    seek (state, current) {
      if (state.timeoutID) {
        clearTimeout(state.timeoutID)
      }
      state.currentDate = new Date(current)
      if (state.isPlaying) {
        state.isPlaying = false
      }
    },
    updateCurrentIndex (state, startIndex) {
      // Updates the current index.
      for (let index = startIndex; index < state.buffer.length - 1; index++) {
        const currentPayload = state.buffer[index]
        const nextPayload = state.buffer[index + 1]
        const currentDate = moment(currentPayload.Date, 'DD.MM.YYYY', false).toDate()
        const nextDate = moment(nextPayload.Date, 'DD.MM.YYYY', false).toDate()
        // If currentDate is greater than state.currentDate, then
        // we return.
        if (currentDate > state.currentDate) {
          return
          // If currentDate is less or equal than state.currentDate and
          // nextDate is greater than state.currentDate, then
          // we return
        } else if (currentDate <= state.currentDate
          && nextDate > state.currentDate) {
          state.currentIndex = index
          return
        }
      }
      // If we're out of the loop, then it means that
      // we're at the last index.
      state.currentIndex = state.buffer.length - 1
    },
    update (state, {
      timeoutID,
      currentDate
    }) {
      state.timeoutID = timeoutID
      state.currentDate = currentDate
    },
    prePlay (state) {
      if (state.hasEnded) {
        state.hasEnded = false
        state.currentDate = state.startDate
        state.currentIndex = 0
      }
    },
    play (state, timeoutID) {
      state.isPlaying = true
      state.timeoutID = timeoutID
    },
    pause (state) {
      state.isPlaying = false
      state.timeoutID = null
    },
    stop (state) {
      state.hasEnded = true
      state.isPlaying = false
      state.timeoutID = null
    }
  },
  actions: {
    seek ({
      commit,
      dispatch,
      state
    }, current) {
      if (state.timeoutID) {
        clearTimeout(state.timeoutID)
      }
      commit('seek', current)
      commit('updateCurrentIndex', 0)
    },
    play ({
      commit,
      dispatch
    }) {
      commit('prePlay')
      const timeoutID = setTimeout(() => {
        dispatch('update')
      }, SimulationTimeoutTime)
      commit('play', timeoutID)
    },
    update ({
      commit,
      dispatch,
      state
    }) {
      let currentDate = new Date(
        state.currentDate.getTime() +
        state.currentDateStep
      )
      // If we are playing faster than receiving
      // data then we pause the simulation until
      // we could receive more data.
      if (currentDate > state.endDate) {
        currentDate = new Date(state.bufferedDate)
        commit('update', { currentDate })
        commit('updateCurrentIndex', 0)
        dispatch('stop')
      } else if (currentDate > state.bufferedDate) {
        currentDate = new Date(state.bufferedDate)
        // TODO: We should pass a "reason" to this
        // function to differentiate between user
        // interaction or buffering.
        commit('update', { currentDate })
        commit('updateCurrentIndex', state.currentIndex)
        dispatch('pause')
      } else {
        const timeoutID = setTimeout(() => {
          dispatch('update')
        }, SimulationTimeoutTime)
        commit('update', {
          timeoutID,
          currentDate
        })
        commit('updateCurrentIndex', state.currentIndex)
      }
    },
    pause ({
      commit,
      state
    }) {
      clearTimeout(state.timeoutID)
      commit('pause')
    },
    stop ({ commit }) {
      commit('stop')
    },
    cancel () {
      if (simulationSocket && simulationSocket.readyState < 2) {
        simulationSocket.close()
      }
    },
    retrieve ({ rootState }) {
      return {
        Weather: {
          LocationCode: rootState.region.type.id,
          UserLatitude: rootState.region.latitude,
          UserLongitude: rootState.region.longitude,
          SeedingDate: moment(rootState.crop.seedingDate).format('YYYY-MM-DD'),
          HarvestDate: moment(rootState.crop.harvestDate).format('YYYY-MM-DD'),
          SimStartDate: moment(rootState.simulation.startDate).format('YYYY-MM-DD'),
          SimStopDate: moment(rootState.simulation.endDate).format('YYYY-MM-DD')
        },
        WaterModelParam: {
          NumLayers: parseInt(rootState.soil.layers, 10),
          LayerThickness: parseInt(rootState.soil.thickness, 10),
          SoilCode: parseInt(rootState.soil.type.id, 10),
          IniNMin1: rootState.soil.initialN[0].amount,
          IniNMin2: rootState.soil.initialN[1].amount,
          IniNMin3: rootState.soil.initialN[2].amount
        },
        CropModelParam: {
          UseFertigation: rootState.fertilizer.fertigation ? 1 : 0,
          Volatilization: {
            unknown: 0,
            low: 1,
            medium: 2,
            high: 3
          }[rootState.fertilizer.volatilization],
          CropType: parseInt(rootState.crop.type.id, 10),
          UserMainYield: rootState.crop.MainYield
        },
        NApplication: rootState.fertilizer.applications.map((application, index, list) => {
          // In case we are at index 0 we create a non-existent application that only
          // contains BBCH because this is the only value we need to interpolate.
          if (application.distribution === Distribution.PERIODICAL) {
            const days = moment(application.toDate).diff(application.fromDate, 'days')
            const applications = []
            for (let day = 0; day < days; day += application.every) {
              applications.push({
                ApplicationDate: moment(application.fromDate).add(day, 'd').format('YYYY-MM-DD'),
                NRate: parseFloat(application.rate),
                FType: parseInt(application.fertilizer, 10)
              })
            }
            return applications
          } else if (application.distribution === Distribution.ONE_TIME) {
            return [{
              ApplicationDate: moment(application.date).format('YYYY-MM-DD'),
              NRate: parseFloat(application.rate),
              FType: parseInt(application.fertilizer, 10)
            }]
          }
        }).flat(1),
        OrgApplication: rootState.fertilizer.organicApplications.map((application) => {
          return {
            ApplicationDate: moment(application.date).format('YYYY-MM-DD'),
            OrgNRate: parseFloat(application.rate),
            OrgFType: parseInt(application.fertilizer, 10)
          }
        }),
        IApplication: rootState.irrigation.applications.map((application) => {
          return {
            ApplicationDate: moment(application.date).format('YYYY-MM-DD'),
            IRate: parseFloat(application.rate),
            IType: parseInt(application.fertilizer, 10)
          }
        })
      }
    },
    async retrieveTest ({ commit }) {
      commit('setDates', {
        startDate: moment('2017-09-30T00:00:00').toDate(),
        endDate: moment('2017-12-30T00:00:00').toDate()
      })
      const module = await import('@/fixtures/input.json')
      return module.default
    },
    async retrieveTestAndRun ({ dispatch }) {
      dispatch('run', await dispatch('retrieveTest'))
    },
    async retrieveAndRun ({ dispatch }) {
      dispatch('run', await dispatch('retrieve'))
    },
    async test ({
      dispatch,
      commit
    }) {
      if (hasEditor) {
        dispatch('editor/clear', null, { root: true })
      }
      commit('setDates', {
        startDate: moment('2017-09-30T00:00:00').toDate(),
        endDate: moment('2017-12-29T00:00:00').toDate()
      })
      commit('started')
      const output = await import('@/fixtures/output.json')
      output.default.forEach((current) => commit('add', current))
    },
    run ({ commit }, simulationData) {
      return new Promise((resolve, reject) => {
        // If we already have an open socket
        if (simulationSocket && simulationSocket.readyState < 2) {
          simulationSocket.close()
        }

        // Create a new WebSocket that we can use.
        // Create a new WebSocket that we can use.
        const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws'
        const host = window.location.host

        simulationSocket = new WebSocket(`${protocol}://${host}/simulation`)
        simulationSocket.onopen = (e) => {
          commit('started')

          // Send data only when the socket is in
          // OPEN state.
          const stringifiedData = JSON.stringify(simulationData)
          simulationSocket.send(stringifiedData)

          return resolve()
        }
        simulationSocket.onclose = (e) => {
          commit('ended')
        }
        simulationSocket.onmessage = (e) => {
          try {
            const messageData = JSON.parse(e.data)
            const {
              type
            } = messageData
            if (type === SimulationMessageType.SIMULATION_ERROR ||
              type === SimulationMessageType.SERVER_ERROR) {
              commit('error', messageData)
            } else if (type === SimulationMessageType.DATA) {
              commit('add', messageData)
            }
          } catch (error) {
            commit('error', error)
          }
        }
        simulationSocket.onerror = (e) => {
          commit('error', e)
          return reject(e)
        }
      })
    }
  }
}
